import type { Lesson } from "@/core/types";
import { criterion, fail, fromCriteria, unsupported } from "@/core/check";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_PDF } from "../makros";

export const lektionPdfUndHandout: Lesson = {
  id: "pdf-und-handout",
  title: "PDF, Handzettel und Notizenseiten",
  summary: "Drei Druckfassungen für drei verschiedene Zwecke.",
  durationMinutes: 9,
  learningGoals: [
    "Die passende Druckfassung zum Zweck wählen",
    "Eine barrierefreie PDF exportieren",
    "Handzettel und Notizenseiten gezielt einsetzen",
  ],
  steps: [
    {
      id: "drei-fassungen",
      title: "Drei Fassungen, drei Zwecke",
      explanation: [
        "Unter „Datei“ → „Drucken“ verbirgt sich hinter der Auswahl „Ganzseitige " +
          "Folien“ ein Menü mit mehreren Fassungen. Drei davon werden regelmäßig " +
          "gebraucht.",
        "Ganzseitige Folien sind die Fassung für den Beamer und für eine PDF, die " +
          "jemand am Bildschirm durchblättert. Handzettel drucken mehrere Folien auf eine " +
          "Seite – die Variante „3 Folien“ setzt rechts daneben Linien zum Mitschreiben " +
          "und ist damit die klassische Fassung zum Verteilen im Raum. Notizenseiten " +
          "zeigen jede Folie mit ihren Sprechernotizen darunter und sind die Fassung für " +
          "die Nachlese.",
        "Die Entscheidung folgt aus der Frage, was die Empfänger damit tun sollen. Wer " +
          "im Raum mitschreiben soll, bekommt Handzettel. Wer den Vortrag nachlesen " +
          "soll, ohne dabei gewesen zu sein, bekommt Notizenseiten. Wer nur einen Blick " +
          "auf die Bilder werfen soll, bekommt die Folien als PDF.",
        "Die Folien im .pptx-Format weiterzugeben ist selten die beste Wahl: Die Datei " +
          "ist größer, lässt sich unbeabsichtigt verändern und sieht auf einem fremden " +
          "Rechner möglicherweise anders aus.",
      ],
      keyPoints: [
        "Datei → Drucken → „Ganzseitige Folien“ öffnet die Auswahl der Fassungen.",
        "Handzettel „3 Folien“ bringt Schreiblinien mit.",
        "Notizenseiten sind die Fassung für die Nachlese.",
      ],
    },
    {
      id: "pdf-exportieren",
      title: "Als PDF exportieren",
      explanation: [
        "Über „Datei“ → „Exportieren“ → „PDF/XPS-Dokument erstellen“ entsteht eine " +
          "PDF. Der entscheidende Teil steckt im Dialog hinter der Schaltfläche " +
          "„Optionen“.",
        "Dort lässt sich erstens festlegen, welche Fassung exportiert wird – auch hier " +
          "stehen Handzettel und Notizenseiten zur Auswahl. Zweitens gibt es das Häkchen " +
          "„Dokumentstrukturtags für Barrierefreiheit“: Es sorgt dafür, dass die PDF eine " +
          "Struktur mitbekommt, sodass ein Screenreader Titel und Inhalt erkennt. Ohne " +
          "dieses Häkchen ist die PDF für Screenreader weitgehend wertlos – und die " +
          "ganze Arbeit aus dem Modul Barrierefreiheit verpufft an der letzten Stelle.",
        "Drittens gibt es die Option „Ausgeblendete Folien einschließen“. Standardmäßig " +
          "ist sie aus, was meistens richtig ist: Reservefolien sollen im Handout nicht " +
          "auftauchen. Wer sie mitgeben will, muss das Häkchen setzen.",
        "Ein Punkt, der überrascht: Animationen sind in der PDF nicht enthalten. Eine " +
          "Folie, deren Inhalte nacheinander erscheinen, zeigt in der PDF alles auf " +
          "einmal. Das ist genau der PDF-Test aus dem Modul Animationen – wenn die Folie " +
          "so unverständlich ist, war sie zu sehr auf die Animation angewiesen.",
      ],
      keyPoints: [
        "Datei → Exportieren → PDF/XPS erstellen → „Optionen“.",
        "„Dokumentstrukturtags für Barrierefreiheit“ anhaken.",
        "Animationen sind in der PDF nicht enthalten.",
      ],
      task: {
        instruction:
          "Speichere deine Präsentation und exportiere sie danach als PDF in denselben Ordner, " +
          "mit demselben Dateinamen.",
        hint: "Datei → Exportieren → PDF/XPS-Dokument erstellen → Optionen: „Dokumentstrukturtags für Barrierefreiheit“ → Veröffentlichen.",
        macro: MAKRO_PDF,
        check: () =>
          pruefeMitMakro(MAKRO_PDF, (bericht) => {
            if (bericht.janein("gespeichert") !== true) {
              return fail("Die Präsentation ist noch nicht gespeichert.", "Erst speichern, dann als PDF exportieren.");
            }
            if (bericht.janein("pfadLesbar") !== true) {
              return unsupported(
                "Die Präsentation liegt in OneDrive oder SharePoint. Dort kann das Prüfmakro den Ordner nicht durchsuchen.",
                "Prüfe selbst, ob die PDF-Datei neben der Präsentation liegt. Danach kannst du den Schritt selbst als erledigt markieren.",
              );
            }
            return fromCriteria(
              [criterion("Neben der Präsentation liegt eine gleichnamige PDF-Datei", bericht.janein("pdf") === true)],
              {
                success: "Die PDF ist erstellt – ohne Animationen, dafür überall gleich lesbar.",
                failure: "Neben der Präsentation liegt noch keine PDF-Datei mit demselben Namen.",
                hint: `Gesucht wird: ${bericht.text("datei") ?? "Präsentationsname.pdf"}`,
              },
            );
          }),
      },
    },
    {
      id: "weitere-formate",
      title: "Weitere Ausgabeformate",
      explanation: [
        "Neben der PDF gibt es drei weitere Formate, die in bestimmten Situationen " +
          "genau richtig sind.",
        "Die Bildschirmpräsentation (.ppsx) öffnet beim Doppelklick direkt im " +
          "Vollbildmodus, ohne dass PowerPoint mit seiner Bearbeitungsoberfläche " +
          "erscheint. Praktisch für Vorführungen an fremden Rechnern und für " +
          "Präsentationen, die jemand nur ansehen soll.",
        "Ein Video – „Datei“ → „Exportieren“ → „Video erstellen“ – nimmt Animationen, " +
          "Übergänge und, falls vorhanden, aufgezeichnete Kommentare mit. Das ist das " +
          "Format für eine Präsentation, die ohne vortragende Person funktionieren muss, " +
          "etwa auf einer Website oder an einem Messestand. Die Anzeigedauer je Folie " +
          "lässt sich im Dialog festlegen oder vorher über " +
          "„Bildschirmpräsentation“ → „Bildschirmpräsentation aufzeichnen“ einsprechen.",
        "Und schließlich lassen sich Folien als Bilder speichern: „Datei“ → „Speichern " +
          "unter“ mit dem Dateityp PNG oder JPEG. PowerPoint fragt dann, ob alle Folien " +
          "oder nur die aktuelle exportiert werden sollen. Das ist der schnellste Weg, " +
          "eine Grafik aus einer Folie in ein anderes Dokument zu bekommen.",
      ],
      keyPoints: [
        "Bildschirmpräsentation (.ppsx) startet beim Doppelklick im Vollbild.",
        "Datei → Exportieren → Video erstellen für selbstlaufende Präsentationen.",
        "Speichern unter → PNG exportiert Folien als Bilder.",
      ],
    },
  ],
};
